import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { functionHeaders, isTokenExpired, setAccessToken } from '@dia/db';
import { forgetCachedReads, supabase } from './supabase';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from './env';

/**
 * The staff session (Section 4, LOCKED pattern).
 *
 * Two steps, and the first happens once per phone. An owner or a manager
 * generates a pairing link, the phone opens it and keeps a device token, and
 * from then on signing in is that token plus a PIN. A bare PIN on a public URL
 * is never enough.
 *
 * The device token is the thing worth protecting here, so it lives in
 * localStorage on the phone and nowhere else, and an owner can revoke it.
 */

export type StaffUser = {
  id: string;
  name: string;
  role: 'crew' | 'manager' | 'owner';
  lang: 'es' | 'en';
};

/**
 * What staff-login answers at sign in. What a person may open follows from
 * user.role alone (CLAUDE.md Section 4); there is no per person list.
 */
export type StaffSession = {
  access_token: string;
  user: StaffUser;
};

export type AuthState = {
  session: StaffSession | null;
  paired: boolean;
  loading: boolean;
  error: { es: string; en: string } | null;
  /** Attempts left before the fifteen minute lockout, when the server said. */
  attemptsLeft: number | null;
  pair: (code: string, label?: string) => Promise<boolean>;
  signIn: (pin: string) => Promise<boolean>;
  signOut: () => void;
};

const DEVICE_KEY = 'dia.ops.device';
const SESSION_KEY = 'dia.ops.session';

const AuthContext = createContext<AuthState | null>(null);

function read<T>(key: string): T | null {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage is unavailable. The session lasts until the app closes.
  }
}

async function callLogin(body: Record<string, unknown>) {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/staff-login`, {
    method: 'POST',
    // Authorization as well as apikey (defect D1). A deployed function sits
    // behind a gate that answers 401 before the function runs when the header
    // is missing, and pairing a phone or typing a PIN is exactly when nobody
    // has a token yet, so the anon key stands in for one.
    headers: functionHeaders({ anonKey: SUPABASE_ANON_KEY }),
    body: JSON.stringify(body),
  });
  return { ok: response.ok, body: (await response.json()) as Record<string, unknown> };
}

/**
 * The stored session, unless its token has expired. A staff session lasts
 * twelve hours; a dead one left in place makes every screen look broken
 * instead of showing the PIN pad.
 */
function readSession(): StaffSession | null {
  const stored = read<StaffSession>(SESSION_KEY);
  if (!stored) return null;
  if (isTokenExpired(stored.access_token)) {
    try {
      globalThis.localStorage?.removeItem(SESSION_KEY);
    } catch {
      // Nothing to clean up.
    }
    return null;
  }
  return stored;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [deviceToken, setDeviceToken] = useState<string | null>(() => read<string>(DEVICE_KEY));
  const [session, setSession] = useState<StaffSession | null>(() => {
    const stored = readSession();
    // Applied now, not in an effect: a child's first request must never go out
    // with the token of a session this browser no longer holds.
    setAccessToken(supabase, stored?.access_token ?? null);
    return stored;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ es: string; en: string } | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);

  useEffect(() => {
    setAccessToken(supabase, session?.access_token ?? null);
  }, [session]);

  const pair = useCallback(async (code: string, label?: string) => {
    setLoading(true);
    setError(null);
    try {
      const { ok, body } = await callLogin({ action: 'pair', code, label });
      if (!ok) {
        setError({ es: String(body.message_es ?? ''), en: String(body.message_en ?? '') });
        return false;
      }
      const token = String(body.device_token);
      setDeviceToken(token);
      write(DEVICE_KEY, token);
      return true;
    } catch {
      setError({
        es: 'No hay señal. Intenta cuando vuelva.',
        en: 'No signal. Try again when it comes back.',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const signIn = useCallback(
    async (pin: string) => {
      if (!deviceToken) {
        setError({
          es: 'Este teléfono no está registrado. Pídele el enlace a tu jefe.',
          en: 'This phone is not paired. Ask your manager for the link.',
        });
        return false;
      }

      setLoading(true);
      setError(null);
      try {
        const { ok, body } = await callLogin({ action: 'sign_in', device_token: deviceToken, pin });
        if (!ok) {
          setError({ es: String(body.message_es ?? ''), en: String(body.message_en ?? '') });
          setAttemptsLeft(typeof body.attempts_left === 'number' ? body.attempts_left : null);
          return false;
        }
        const next = {
          access_token: String(body.access_token),
          user: body.user as StaffUser,
        };
        // One person at a time. A PIN sign in ends any owner session this
        // browser still holds, or the two would fight over who is using it.
        void supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
        forgetCachedReads();
        setSession(next);
        setAttemptsLeft(null);
        write(SESSION_KEY, next);
        return true;
      } catch {
        setError({
          es: 'No hay señal. Intenta cuando vuelva.',
          en: 'No signal. Try again when it comes back.',
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [deviceToken],
  );

  const signOut = useCallback(() => {
    // Signing out ends the session. The phone stays paired, because pairing it
    // again needs a manager and the crew should not need one every morning.
    setSession(null);
    forgetCachedReads();
    try {
      globalThis.localStorage?.removeItem(SESSION_KEY);
    } catch {
      // Nothing to clean up.
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      paired: Boolean(deviceToken),
      loading,
      error,
      attemptsLeft,
      pair,
      signIn,
      signOut,
    }),
    [session, deviceToken, loading, error, attemptsLeft, pair, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth needs an AuthProvider above it.');
  return ctx;
}
